import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {AppState} from "../../reducers/RootReducer";
import {  StateObservable, ofType } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {UserViewSettings, UserViewSettingsProp } from "../../models/common/ViewSettings";
import {getUrlParamsFromViewSettings} from "../../config/RouteConfig";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import { downloadFileHelper } from "../../helpers/FileHelpers";
import {ProjectSolvencyReport} from "../../models/reporting/ProjectSolvencyReport";
import {ProjectSolvencyReportModule} from "../../models/reporting/ProjectSolvencyReportModule";
import {ExportTypes} from "../../models/common/Export";
import {FileData} from "../../models/common/FileData";
import {Identifier} from "../../models/common/Identifier";

export type ProjectSolvencyReportActionTypes = FetchProjectSolvencyReportActionTypes
    | FetchProjectSolvencyReportByProjectActionTypes

export interface FetchProjectSolvencyReportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_PROJECT_SOLVENCY_REPORT > {
    projectionPeriodInMonths: number
}
export interface FetchProjectSolvencyReportSuccessAction extends ReduxAction<typeof FETCH_PROJECT_SOLVENCY_REPORT_SUCCESS> {
    reports: ApiReturnType< ProjectSolvencyReport[] >
}
export interface FetchProjectSolvencyReportErrorAction extends ErrorAction<typeof FETCH_PROJECT_SOLVENCY_REPORT_ERROR> {
}

export type FetchProjectSolvencyReportActionTypes = FetchProjectSolvencyReportAction
    | FetchProjectSolvencyReportSuccessAction
    | FetchProjectSolvencyReportErrorAction


export const FETCH_PROJECT_SOLVENCY_REPORT = "FETCH_PROJECT_SOLVENCY_REPORT";
export const FETCH_PROJECT_SOLVENCY_REPORT_SUCCESS = "FETCH_PROJECT_SOLVENCY_REPORT_SUCCESS";
export const FETCH_PROJECT_SOLVENCY_REPORT_ERROR = "FETCH_PROJECT_SOLVENCY_REPORT_ERROR";

export function fetchProjectSolvencyReport(
    projectionPeriodInMonths: number,
    userViewSettings: UserViewSettings
): FetchProjectSolvencyReportAction {
    return {
        type: FETCH_PROJECT_SOLVENCY_REPORT,
        userViewSettings,
        projectionPeriodInMonths
    };
}
export function fetchProjectSolvencyReportSuccess( reports: ApiReturnType< ProjectSolvencyReport[] > ): FetchProjectSolvencyReportSuccessAction {
    return { type: FETCH_PROJECT_SOLVENCY_REPORT_SUCCESS, reports };
}
export function fetchProjectSolvencyReportError( error: Error ): FetchProjectSolvencyReportErrorAction {
    return {type: FETCH_PROJECT_SOLVENCY_REPORT_ERROR, error };
}

export function fetchProjectSolvencyReportEpic (
    action$: Observable< FetchProjectSolvencyReportAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_SOLVENCY_REPORT ),
        switchMap( action => {
            const settingPath = getUrlParamsFromViewSettings( action.userViewSettings );
            const path = `${ APP_CONFIG.baseWeb }reporting/projects/solvency/${ action.projectionPeriodInMonths }${ settingPath }`;
            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchProjectSolvencyReportSuccess( response.response as ApiReturnType< ProjectSolvencyReport[] > ) ),
                catchError( error => of(
                    fetchProjectSolvencyReportError(error ),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse(
                            language,
                            error.status,
                            ProjectSolvencyReportModule.identifier,
                            "fetchProjectSolvencyReport"
                        )
                    )
                ) )
            )
        })
    );
}


/**
 * FETCH SOLVENCY REPORT BY PROJECT
 */
export interface FetchProjectSolvencyReportByProjectAction extends ReduxAction<typeof FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT > {
    projectId: Identifier<number>
    projectionPeriodInMonths: number
}
export interface FetchProjectSolvencyReportByProjectSuccessAction extends ReduxAction<typeof FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_SUCCESS> {
    report: ProjectSolvencyReport
}
export interface FetchProjectSolvencyReportByProjectErrorAction extends ErrorAction<typeof FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_ERROR> {
}

export type FetchProjectSolvencyReportByProjectActionTypes = FetchProjectSolvencyReportByProjectAction
    | FetchProjectSolvencyReportByProjectSuccessAction
    | FetchProjectSolvencyReportByProjectErrorAction


export const FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT = "FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT";
export const FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_SUCCESS = "FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_SUCCESS";
export const FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_ERROR = "FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_ERROR";

export function fetchProjectSolvencyReportByProject(
    projectId: Identifier<number>,
    projectionPeriodInMonths: number
): FetchProjectSolvencyReportByProjectAction {
    return {
        type: FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT,
        projectionPeriodInMonths,
        projectId
    };
}
export function fetchProjectSolvencyReportByProjectSuccess( report: ProjectSolvencyReport ): FetchProjectSolvencyReportByProjectSuccessAction {
    return { type: FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_SUCCESS, report };
}
export function fetchProjectSolvencyReportByProjectError( error: Error ): FetchProjectSolvencyReportByProjectErrorAction {
    return {type: FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT_ERROR, error };
}

export function fetchProjectSolvencyReportByProjectEpic (
    action$: Observable< FetchProjectSolvencyReportByProjectAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_SOLVENCY_REPORT_BY_PROJECT ),
        switchMap( action => {
            const path = `${ APP_CONFIG.baseWeb }reporting/projects/${ action.projectId.rawValue }/solvency/${ action.projectionPeriodInMonths }`;
            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchProjectSolvencyReportByProjectSuccess(
                    response.response as ProjectSolvencyReport
                ) ),
                catchError( error => of(
                    fetchProjectSolvencyReportByProjectError(error ),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse(
                            language,
                            error.status,
                            ProjectSolvencyReportModule.identifier,
                            "fetchProjectSolvencyReportByProject"
                        )
                    )
                ) )
            )
        })
    );
}


/**
 * export
 */
export interface FetchProjectSolvencyReportExportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_PROJECT_REPORT_EXPORT > {
    projectionPeriodInMonths: number,
    fileData: FileData
    exportTypes: ExportTypes
}
export interface FetchProjectSolvencyReportExportSuccessAction extends ReduxAction<typeof FETCH_PROJECT_REPORT_EXPORT_SUCCESS> {
}
export interface FetchProjectSolvencyReportExportErrorAction extends ErrorAction<typeof FETCH_PROJECT_REPORT_EXPORT_ERROR> {
}

export type FetchProjectSolvencyReportExportActionTypes = FetchProjectSolvencyReportExportAction
    | FetchProjectSolvencyReportExportSuccessAction
    | FetchProjectSolvencyReportExportErrorAction

export const FETCH_PROJECT_REPORT_EXPORT = "FETCH_PROJECT_REPORT_EXPORT";
export const FETCH_PROJECT_REPORT_EXPORT_SUCCESS = "FETCH_PROJECT_REPORT_EXPORT_SUCCESS";
export const FETCH_PROJECT_REPORT_EXPORT_ERROR = "FETCH_PROJECT_REPORT_EXPORT_ERROR";

export function fetchProjectSolvencyReportExport(
    projectionPeriodInMonths: number,
    exportTypes: ExportTypes,
    userViewSettings: UserViewSettings,
    fileData: FileData
): FetchProjectSolvencyReportExportAction {
    return {
        type: FETCH_PROJECT_REPORT_EXPORT,
        projectionPeriodInMonths,
        userViewSettings ,
        exportTypes,
        fileData
    };
}
export function fetchProjectSolvencyReportExportSuccess(): FetchProjectSolvencyReportExportSuccessAction {
    return { type: FETCH_PROJECT_REPORT_EXPORT_SUCCESS };
}
export function fetchProjectSolvencyReportExportError(
    error: Error
): FetchProjectSolvencyReportExportErrorAction {
    return {type: FETCH_PROJECT_REPORT_EXPORT_ERROR, error };
}

export function fetchProjectSolvencyReportExportEpic (
    action$: Observable< FetchProjectSolvencyReportExportAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_REPORT_EXPORT ),
        switchMap( action => {
            const settingPath = getUrlParamsFromViewSettings( action.userViewSettings );
            const path = `${ APP_CONFIG.baseWeb }reporting/projects/solvency/export/${ action.exportTypes }/${ action.projectionPeriodInMonths }${ settingPath }`;
            const language = currentLanguageFromStore( store$ );

            return ajax({
                ...APP_CONFIG.getRequestObject(path),
                responseType: "blob",
                async: false
            }).pipe(
                map( response => {
                    console.log( response )
                    //@ts-ignore
                    downloadFileHelper( response.response, action.fileData );

                    return fetchProjectSolvencyReportExportSuccess();
                } ),
                catchError( error => {

                    return of(
                        fetchProjectSolvencyReportExportError(error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            createErrorResponse(
                                language,
                                error.status ?? 200,
                                "",
                                "fetchProjectSolvencyReportExport"
                            )
                        )
                    )
                } )
            )
        })
    );
}